import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useConfigMapJson } from "@shared/api/hooks";
import { useWarehouses, useChoices } from "@features/mx/hooks";
import { useUpdatePhysicalStatus } from "@features/mx/resources/hook";

interface UpdatePhysicalStatusModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resource: any;
  onSuccess?: (status: string, details: any) => void;
}

export const UpdatePhysicalStatusModal: React.FC<UpdatePhysicalStatusModalProps> = ({
  open,
  onOpenChange,
  resource,
  onSuccess,
}) => {
  const remoteUtilityId = useRemoteUtilityId();
  const [watchPremise, setWatchPremise] = useState<string>("");

  const { data: warehousesResponse } = useWarehouses(remoteUtilityId);
  const { data: physicalStatusChoices } = useChoices({
    choice: "physical_status",
    remoteUtilityId,
  });
  const { data: premiseConfig } = useConfigMapJson(
    "territory_json",
    remoteUtilityId,
    "premise"
  );
  const { data: unitConfig } = useConfigMapJson(
    "territory_json",
    remoteUtilityId,
    "unit",
    watchPremise || undefined
  );

  const warehouseOptions = useMemo(() => {
    const results = warehousesResponse?.result || [];
    return results.map((w: any) => ({
      label: w.name,
      value: w.id,
    }));
  }, [warehousesResponse]);

  const physicalStatusOptions = useMemo(() => {
    return Array.isArray(physicalStatusChoices)
      ? physicalStatusChoices.map((item: any) => ({
          label: item.value,
          value: item.key,
        }))
      : [];
  }, [physicalStatusChoices]);

  const premiseOptions = useMemo(() => {
    return Array.isArray(premiseConfig)
      ? premiseConfig.map((item: any) => ({
          label: item.name || item,
          value: item.code || item,
        }))
      : [];
  }, [premiseConfig]);

  const unitOptions = useMemo(() => {
    return Array.isArray(unitConfig)
      ? unitConfig.map((item: any) => ({
          label: item.name || item,
          value: item.code || item,
        }))
      : [];
  }, [unitConfig]);

  const formFields: FormField[] = [
    {
      name: "physicalStatus",
      type: "select",
      required: true,
      label: "Physical status",
      placeholder: "Select physical status",
      options: physicalStatusOptions,
    },
    {
      name: "remark",
      type: "textarea",
      required: false,
      label: "Remark",
      placeholder: "Add a remark about this status change...",
      helperText: "Optional. Describe the reason or any additional context for this status update.",
      className:"mt-4"
    },
    {
      name: "warehouse",
      type: "select",
      required: true,
      label: "Warehouse",
      placeholder: "Select warehouse",
      options: warehouseOptions,
      helperText: "The storage facility where this resource will be held until it is ready for installation.",
      showWhen: (formValues) => [2, 3, 4].includes(Number(formValues?.physicalStatus)),
      className:"mt-4"
    },
    {
      name: "premise",
      type: "select",
      required: true,
      label: "Premise",
      placeholder: "Select premise",
      options: premiseOptions,
      helperText: "The property or location where this resource is physically placed.",
      showWhen: (formValues) => Number(formValues?.physicalStatus) === 0,
      className:"mt-4"
    },
    {
      name: "unit",
      type: "select",
      required: true,
      label: "Unit",
      placeholder: "Select unit",
      options: unitOptions,
      helperText: "The specific unit within the selected premise to assign this resource to.",
      showWhen: (formValues) => Number(formValues?.physicalStatus) === 0,
      disabled: (formValues) => !formValues.premise,
      className:"mt-4"
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
  });

  const currentPremise = form.watch("premise");
  useEffect(() => {
    setWatchPremise(currentPremise || "");
  }, [currentPremise]);

  const watchedStatus = form.watch("physicalStatus");
  const { mutate: updateStatusMutate, isPending: isUpdating } = useUpdatePhysicalStatus();

  const getInitialStatusKey = (statusString: string) => {
    const defaultMapping: Record<string, number> = {
      "Installed": 0,
      "Retired": 4,
      "In Warehouse": 2,
      "Damaged": 3,
    };
    if (Array.isArray(physicalStatusChoices)) {
      const match = physicalStatusChoices.find((c: any) => c.value === statusString);
      if (match) return match.key;
    }
    return defaultMapping[statusString] ?? "";
  };

  // Reset when opening
  useEffect(() => {
    if (open) {
      form.reset({
        physicalStatus: getInitialStatusKey(resource.physicalStatus),
        remark: "",
        warehouse: "",
        premise: resource?.premise,
        unit: resource?.unitCode,
      });
    }
  }, [open, resource, physicalStatusChoices]);

  const handleSubmit = (data: any) => {
    const payload: any = {
      physical_status: Number(data.physicalStatus),
      remark: data.remark || "",
    };

    if (Number(data.physicalStatus) === 0) {
      payload.premise = data.premise;
      payload.unit_code = data.unit;
    } else if ([2, 3, 4].includes(Number(data.physicalStatus))) {
      payload.warehouse = data.warehouse;
    }

    updateStatusMutate(
      { resourceId: resource?.id, payload },
      {
        onSuccess: () => {
          if (onSuccess) {
            onSuccess(data.physicalStatus, data);
          }
          onOpenChange(false);
        },
      }
    );
  };

  const renderCustomFooter = () => (
    <div className="flex justify-end gap-3 mt-6">
      <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isUpdating}>
        Cancel
      </Button>
      <Button type="submit" disabled={!form.formState.isValid || isUpdating}>
        {isUpdating ? "Saving..." : "Save"}
      </Button>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Update Physical Status</DialogTitle>
          <DialogDescription className="text-sm text-gray-500">
            Select the new physical status for resource <span className="font-semibold">{resource?.id}</span>.
          </DialogDescription>
        </DialogHeader>

        {Number(watchedStatus) === 2 && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-3.5 text-xs">
            The resource will be placed in storage. You must specify the warehouse where it will be held.
          </div>
        )}

        <div className="py-2">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            config={{
              showSubmitButton: false,
              classes:FIELD_CLASSES
            }}
            renderCustomFooter={renderCustomFooter}
            gridColumns={1}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
